import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, User, Phone, MessageCircle, Mail, Instagram, Heart, Pencil, Briefcase, Wrench, Languages, GraduationCap, Car } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const FLAG = { Shqip: "🇦🇱", Suedisht: "🇸🇪", Anglisht: "🇬🇧", Gjermanisht: "🇩🇪", Norvegjisht: "🇳🇴", Daneze: "🇩🇰", Frëngjisht: "🇫🇷", Italisht: "🇮🇹" };

function Section({ icon: Icon, title, children }) {
  return (
    <section className="px-4 mt-5">
      <div className="flex items-center gap-2 mb-2">{Icon && <Icon className="w-4 h-4 text-amber-500" />}<h2 className="text-lg font-extrabold text-slate-900">{title}</h2></div>
      {children}
    </section>
  );
}

export default function SeekerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, requireAuth } = useAuth();
  const [l, setL] = useState(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(() => {
    api.get(`/listings/${id}`).then((r) => {
      setL(r.data);
      api.get("/favorites/ids").then((f) => setSaved(f.data.includes(r.data.id))).catch(() => {});
    }).catch(() => toast.error("Profili nuk u gjet"));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!l) return <div className="p-8 text-center text-slate-400">Duke ngarkuar...</div>;
  const d = l.data || {};
  const photo = l.images && l.images[0];
  const isOwner = user && user !== false && l.owner && l.owner.id === user.id;
  const waNum = (l.whatsapp || "").replace(/[^0-9]/g, "");

  const toggleSave = () => requireAuth(async () => {
    try { const r = await api.post(`/favorites/${l.id}`); setSaved(r.data.saved); toast.success(r.data.saved ? "U ruajt" : "U hoq"); } catch { toast.error("Gabim"); }
  });

  const bigContacts = [
    l.phone && { icon: Phone, label: "Telefon", href: `tel:${l.phone.replace(/\s/g, "")}`, cls: "from-emerald-500 to-emerald-700" },
    waNum && { icon: MessageCircle, label: "WhatsApp", href: `https://wa.me/${waNum}`, cls: "from-green-400 to-green-600" },
    l.email && { icon: Mail, label: "Email", href: `mailto:${l.email}`, cls: "from-slate-700 to-slate-900" },
    l.instagram && { icon: Instagram, label: "Instagram", href: l.instagram.startsWith("http") ? l.instagram : `https://instagram.com/${l.instagram}`, cls: "from-pink-500 to-fuchsia-600" },
  ].filter(Boolean);

  return (
    <div className="gs-fade pb-10 bg-slate-50 min-h-screen">
      <div className="px-4 pt-3">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-yellow-400 to-amber-500 p-5 shadow-lg">
          <div className="absolute -right-10 -top-10 w-44 h-44 rounded-full bg-white/10" />
          <div className="absolute right-6 bottom-6 w-28 h-28 rounded-full bg-white/10" />

          <div className="relative z-10 flex items-center justify-between mb-3">
            <button data-testid="seeker-detail-back" onClick={() => nav(-1)} className="p-1.5 rounded-full bg-white/25 text-slate-900"><ArrowLeft className="w-5 h-5" /></button>
            <div className="flex gap-2">
              {isOwner && <button data-testid="seeker-edit" onClick={() => nav(`/ndrysho/${l.id}`)} className="p-2 rounded-full bg-white/25 text-slate-900"><Pencil className="w-4 h-4" /></button>}
              <button data-testid="seeker-detail-save" onClick={toggleSave} className="p-2 rounded-full bg-white/25 text-slate-900"><Heart className={`w-4 h-4 ${saved ? "fill-slate-900" : ""}`} /></button>
            </div>
          </div>

          <div className="relative z-10 flex items-center gap-4">
            <div className="relative shrink-0">
              <div className="w-24 h-24 rounded-full overflow-hidden ring-4 ring-white bg-amber-50 flex items-center justify-center">
                {photo ? <img src={photo} alt={l.title} className="w-full h-full object-cover" /> : <span className="text-4xl font-extrabold text-emerald-700">{l.title?.[0]?.toUpperCase() || "?"}</span>}
              </div>
              <span className="absolute bottom-1 right-1 w-6 h-6 rounded-full bg-green-500 ring-4 ring-white" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-extrabold text-slate-900 leading-tight break-words">{l.title}</h1>
              <div className="flex items-center gap-1.5 mt-1.5 text-slate-800 font-semibold"><Briefcase className="w-4 h-4 shrink-0" /><span className="truncate">{l.subcategory}</span></div>
              <div className="flex items-center gap-1.5 mt-1 text-slate-800/90 text-sm font-medium"><MapPin className="w-4 h-4 shrink-0" /><span className="truncate">{l.city}{l.country ? `, ${l.country}` : ""}</span></div>
            </div>
          </div>

          {bigContacts.length > 0 && (
            <div className="relative z-10 grid grid-cols-3 gap-2.5 mt-5">
              {bigContacts.map((c) => { const Icon = c.icon; return (
                <a key={c.label} data-testid={`seeker-contact-${c.label}`} href={c.href} target="_blank" rel="noreferrer" className={`flex items-center justify-center gap-1.5 rounded-2xl py-3.5 bg-gradient-to-br ${c.cls} text-white shadow-md active:scale-95 transition-transform`}>
                  <Icon className="w-5 h-5 shrink-0" /><span className="text-sm font-bold">{c.label}</span>
                </a>
              ); })}
            </div>
          )}
        </div>
      </div>

      {l.description && (
        <Section icon={User} title="Rreth meje">
          <div className="bg-white rounded-2xl border border-slate-100 p-3">
            <div className="bg-slate-50 rounded-xl p-4 text-sm text-slate-600 leading-relaxed">{l.description}</div>
          </div>
        </Section>
      )}

      {(d.seeking || (d.job_types || []).length > 0) && (
        <Section icon={Briefcase} title="Po kërkoj">
          <div className="bg-white rounded-2xl border border-slate-100 p-4">
            {d.seeking && <p className="text-sm text-slate-700 font-medium mb-2">{d.seeking}</p>}
            <div className="flex flex-wrap gap-2">{(d.job_types || []).map((t) => <span key={t} className="bg-yellow-100 text-yellow-700 text-xs font-semibold px-3 py-1.5 rounded-full">{t}</span>)}</div>
          </div>
        </Section>
      )}

      {(d.experience_level || d.experience_years || d.prev_positions || d.field) && (
        <Section icon={Briefcase} title="Përvoja">
          <div className="bg-white rounded-2xl border border-slate-100 p-4">
            <div className="relative pl-6">
              <span className="absolute left-0 top-1.5 w-3 h-3 rounded-full bg-amber-400 ring-4 ring-amber-100" />
              <span className="absolute left-[5px] top-5 bottom-0 w-0.5 bg-amber-200" />
              <h3 className="font-bold text-slate-900">{d.field || l.subcategory}</h3>
              <p className="text-sm text-slate-500 mt-0.5">{l.city}{l.country ? `, ${l.country}` : ""}</p>
              {(d.experience_years || d.experience_level) && <p className="text-sm text-slate-400 mt-0.5">{[d.experience_years, d.experience_level].filter(Boolean).join(" · ")}</p>}
              {d.prev_positions && <p className="text-sm text-slate-600 mt-2 whitespace-pre-line">{d.prev_positions}</p>}
            </div>
          </div>
        </Section>
      )}

      {(d.skills || []).length > 0 && (
        <Section icon={Wrench} title="Aftësitë"><div className="flex flex-wrap gap-2">{d.skills.map((s) => <span key={s} className="bg-amber-50 text-amber-700 text-sm font-semibold px-3 py-1.5 rounded-full border border-amber-200">{s}</span>)}</div></Section>
      )}

      {(d.languages || []).length > 0 && (
        <Section icon={Languages} title="Gjuhët"><div className="flex flex-wrap gap-2">{d.languages.map((s) => <span key={s} className="bg-blue-50 text-blue-700 text-sm font-semibold px-3 py-1.5 rounded-full border border-blue-200">{FLAG[s] || ""} {s}</span>)}</div></Section>
      )}

      {d.education && <Section icon={GraduationCap} title="Arsimi dhe kualifikimet"><p className="text-sm text-slate-600 leading-relaxed bg-white rounded-2xl border border-slate-100 p-4 whitespace-pre-line">{d.education}</p></Section>}

      {(d.licenses || []).length > 0 && (
        <Section icon={Car} title="Patenta e shoferit"><div className="flex flex-wrap gap-2">{d.licenses.map((s) => <span key={s} className="bg-slate-800 text-white text-sm font-bold px-3.5 py-1.5 rounded-lg">{s}</span>)}</div></Section>
      )}
    </div>
  );
}
